// Utility helpers for normalizing video URLs (especially YouTube) to embeddable formats

/** Extract Google Drive file id from common share / open / uc / thumbnail /usercontent URLs. */
export function getGoogleDriveFileId(rawUrl) {
  if (!rawUrl) return null;
  try {
    const url = new URL(String(rawUrl).trim());
    const host = url.hostname.toLowerCase();

    // https://lh3.googleusercontent.com/d/FILE_ID or /d/FILE_ID=w2000
    if (host.endsWith('googleusercontent.com')) {
      const match = url.pathname.match(/\/d\/([^/=?#]+)/);
      if (match?.[1]) return match[1];
    }

    // https://drive.usercontent.google.com/download?id=FILE_ID&export=view
    if (host === 'drive.usercontent.google.com') {
      const id = url.searchParams.get('id');
      if (id) return id;
    }

    if (host !== 'drive.google.com' && host !== 'docs.google.com') return null;

    const filePathMatch = url.pathname.match(/\/file\/d\/([^/]+)/);
    if (filePathMatch?.[1]) return filePathMatch[1];

    const id = url.searchParams.get('id');
    if (
      id &&
      (url.pathname === '/open' ||
        url.pathname.startsWith('/uc') ||
        url.pathname.startsWith('/thumbnail'))
    ) {
      return id;
    }

    return null;
  } catch {
    return null;
  }
}

/** Google Drive share links must use /preview in an iframe — /view is HTML, not a video file. */
export function getGoogleDriveEmbedUrl(rawUrl) {
  const fileId = getGoogleDriveFileId(rawUrl);
  if (!fileId) return null;
  return `https://drive.google.com/file/d/${fileId}/preview`;
}

/**
 * Convert Google Drive share / view / open links to a URL that works in <img src>.
 * Share links like /file/d/ID/view are HTML pages and will not render as images.
 * Prefer googleusercontent host — browsers often block drive.google.com/uc hotlinks via Referer.
 */
export function getGoogleDriveDirectMediaUrl(rawUrl) {
  const fileId = getGoogleDriveFileId(rawUrl);
  if (!fileId) return null;
  return `https://lh3.googleusercontent.com/d/${fileId}`;
}

/**
 * Convert Google Drive share links to a downloadable URL for native <audio> / media proxy.
 * Image-oriented lh3.googleusercontent URLs do not work reliably for audio playback.
 */
export function getGoogleDriveDirectAudioUrl(rawUrl) {
  const fileId = getGoogleDriveFileId(rawUrl);
  if (!fileId) return null;
  return `https://drive.usercontent.google.com/download?id=${fileId}&export=download`;
}

/** Ordered fallbacks when the primary Drive audio URL fails to load. */
export function getGoogleDriveAudioSrcCandidates(rawUrl) {
  const fileId = getGoogleDriveFileId(rawUrl);
  if (!fileId) return [];
  return [
    `https://drive.usercontent.google.com/download?id=${fileId}&export=download`,
    `https://drive.google.com/uc?export=download&id=${fileId}`,
  ];
}

/** Ordered fallbacks when the primary Drive image URL fails to load. */
export function getGoogleDriveImageSrcCandidates(rawUrl) {
  const fileId = getGoogleDriveFileId(rawUrl);
  if (!fileId) return [];
  return [
    `https://lh3.googleusercontent.com/d/${fileId}`,
    `https://lh3.googleusercontent.com/d/${fileId}=w2000`,
    `https://drive.google.com/thumbnail?id=${fileId}&sz=w2000`,
    `https://drive.google.com/uc?export=view&id=${fileId}`,
  ];
}

/** Zoho Meeting recording hosts (regional portals). */
function isZohoMeetingHost(hostname) {
  const host = String(hostname || '').toLowerCase();
  return host.startsWith('meeting.zoho') || host === 'meeting.zohocloud.ca';
}

/** True for Zoho Meeting share / play recording pages (not direct MP4 CDN links). */
export function isZohoMeetingRecordingUrl(rawUrl) {
  if (!rawUrl) return false;
  try {
    const url = new URL(String(rawUrl).trim());
    if (!isZohoMeetingHost(url.hostname)) return false;
    return /\/meeting\/(public\/)?videoprv\b/i.test(url.pathname);
  } catch {
    return false;
  }
}

/**
 * Zoho Meeting recordings play in an iframe with view=embed.
 * Share links look like: .../meeting/public/videoprv?recordingId=...
 * Play links look like: .../meeting/videoprv?...&view=embed
 */
export function getZohoMeetingEmbedUrl(rawUrl) {
  if (!rawUrl) return null;
  try {
    const url = new URL(String(rawUrl).trim());
    if (!isZohoMeetingHost(url.hostname)) return null;
    if (!/\/meeting\/(public\/)?videoprv\b/i.test(url.pathname)) return null;

    const embed = new URL(url.toString());
    embed.searchParams.set('view', 'embed');
    return embed.toString();
  } catch {
    return null;
  }
}

export function getVimeoEmbedUrl(rawUrl) {
  if (!rawUrl) return null;
  try {
    const url = new URL(String(rawUrl).trim());
    const host = url.hostname.toLowerCase();
    if (host === 'player.vimeo.com') return url.toString();
    if (host !== 'vimeo.com' && !host.endsWith('.vimeo.com')) return null;

    const parts = url.pathname.split('/').filter(Boolean);
    const id = parts.find((p) => /^\d+$/.test(p));
    if (!id) return null;

    const embed = new URL(`https://player.vimeo.com/video/${id}`);
    const allowedParams = ['autoplay', 'muted', 'loop', 't'];
    for (const key of allowedParams) {
      const value = url.searchParams.get(key);
      if (value) embed.searchParams.set(key, value);
    }
    return embed.toString();
  } catch {
    return null;
  }
}

/**
 * Choose iframe vs native <video> for a URL. Loop does not change the player mode —
 * Google Drive always stays in iframe preview (direct uc URLs break in <video>).
 */
export function getVideoPlaybackSource(rawUrl) {
  const trimmed = String(rawUrl || '').trim();
  if (!trimmed) return { type: 'none', src: '', embedUrl: null };

  const embedUrl = getVideoEmbedUrl(trimmed);
  if (embedUrl) {
    return { type: 'embed', src: trimmed, embedUrl };
  }

  return { type: 'native', src: resolveDirectMediaUrl(trimmed), embedUrl: null };
}

/** Normalize cloud share links to a direct URL for native <video> / <img>. */
export function resolveDirectMediaUrl(rawUrl) {
  if (!rawUrl || typeof rawUrl !== 'string') return rawUrl;
  const trimmed = rawUrl.trim();
  if (!trimmed) return trimmed;

  const driveDirect = getGoogleDriveDirectMediaUrl(trimmed);
  if (driveDirect) return driveDirect;

  try {
    const url = new URL(trimmed);
    const host = url.hostname.toLowerCase();
    if (host.includes('dropbox.com')) {
      url.searchParams.set('raw', '1');
      url.searchParams.delete('dl');
      return url.toString();
    }
    if (host.includes('zohopublic.com') && !/\.(html?|php|asp)/i.test(url.pathname)) {
      return url.toString();
    }
  } catch {
    // ignore
  }
  return trimmed;
}

/** Normalize cloud share links for native <audio> (Drive uses download URLs, not lh3). */
export function resolveDirectAudioUrl(rawUrl) {
  if (!rawUrl || typeof rawUrl !== 'string') return rawUrl;
  const trimmed = rawUrl.trim();
  if (!trimmed) return trimmed;

  const driveAudio = getGoogleDriveDirectAudioUrl(trimmed);
  if (driveAudio) return driveAudio;

  try {
    const url = new URL(trimmed);
    const host = url.hostname.toLowerCase();
    if (host.includes('dropbox.com')) {
      url.searchParams.set('raw', '1');
      url.searchParams.delete('dl');
      return url.toString();
    }
    if (host.includes('zohopublic.com') && !/\.(html?|php|asp)/i.test(url.pathname)) {
      return url.toString();
    }
  } catch {
    // ignore
  }
  return trimmed;
}

/** @deprecated Use resolveDirectMediaUrl */
export const resolveDirectVideoUrl = resolveDirectMediaUrl;

/** Embed URL for iframe-based players (YouTube, Vimeo, Google Drive, Zoho Meeting). */
export function getVideoEmbedUrl(rawUrl) {
  return (
    getYouTubeEmbedUrl(rawUrl) ||
    getGoogleDriveEmbedUrl(rawUrl) ||
    getZohoMeetingEmbedUrl(rawUrl) ||
    getVimeoEmbedUrl(rawUrl)
  );
}

/**
 * True when the URL should load in an iframe (YouTube / Vimeo / Google Drive / Zoho Meeting), not <video>.
 * Direct file URLs (S3/Spaces, signed URLs with query strings, .mov, etc.) must use <video>.
 */
export function shouldUseVideoEmbedIframe(resolvedUrl) {
  if (!resolvedUrl || typeof resolvedUrl !== 'string') return false;
  const trimmed = resolvedUrl.trim();
  if (!trimmed) return false;
  if (/^(data:video\/|blob:)/i.test(trimmed)) return false;
  return !!getVideoEmbedUrl(trimmed);
}

// Convert various YouTube URL formats to an embeddable /embed/... URL
export function getYouTubeEmbedUrl(rawUrl) {
  if (!rawUrl) return null;
  try {
    const url = new URL(rawUrl);

    // Standard watch URL: https://www.youtube.com/watch?v=VIDEO_ID
    if (url.hostname.includes('youtube.com') || url.hostname.includes('youtu.be')) {
      let videoId = null;
      if (url.hostname.includes('youtu.be')) {
        // Short URL: https://youtu.be/VIDEO_ID
        videoId = url.pathname.split('/').filter(Boolean)[0] || null;
      } else if (url.pathname === '/watch') {
        videoId = url.searchParams.get('v');
      } else if (url.pathname.startsWith('/shorts/')) {
        // Shorts: https://www.youtube.com/shorts/VIDEO_ID
        videoId = url.pathname.split('/').filter(Boolean)[1] || null;
      } else if (url.pathname.startsWith('/embed/')) {
        // Already an embed
        videoId = url.pathname.split('/').filter(Boolean)[1] || null;
      }
      if (!videoId) return null;
      const embed = new URL(`https://www.youtube.com/embed/${videoId}`);
      // Preserve some useful params if present
      const allowedParams = ['start', 'end', 't', 'mute', 'autoplay', 'controls'];
      for (const key of allowedParams) {
        const value = url.searchParams.get(key);
        if (value) {
          embed.searchParams.set(key, value);
        }
      }
      return embed.toString();
    }
  } catch {
    // If URL parsing fails, fall through and return null
  }
  return null;
}
